<template>
	<div v-if="isLoading" class="product loader">
		<div>
			<div class="product-icon loader" />
			<div class="product-details">
				<ul class="product-list">
					<li class="loader" />
					<li class="loader" />
					<li class="loader" />
					<li class="loader" />
				</ul>
			</div>
		</div>
		<div class="product-action loader" />
	</div>

	<div v-if="!isLoading && (['enabled', 'stats', 'reporting'].includes(data.state))" class="product" :class="[title.replaceAll(' ', '-')]">
		<div class="product-top">
			<div class="product-icon">
				<span class="icon">
					<i />
				</span>
			</div>

			<div class="product-action">
				<template v-if="data.state === 'enabled'">
					<span v-if="data?.env" class="badge badge-danger mr-5 env-badge" v-text="data?.env" />
					<router-link v-if="!data.open_link.startsWith('http')" :to="data.open_link" class="btn" title="Open">
						Open
					</router-link>
					<a
						v-if="data.open_link.startsWith('http') && data.state !== 'stats'"
						class="btn"
						target="_blank"
						:href="data.open_link"
						title="Open"
					>Open</a>
				</template>
			</div>
		</div>

		<div class="product-details">
			<ul class="product-list">
				<li v-for="(item, i) in data.blocks" :key="i">
					<component
						:is="getLinkType(item)"
						:href="item.link ?? data.open_link"
						:to="item.link ?? data.open_link"
						:title="item?.tooltip ?? ''"
						class="product-info-row"
					>
						<span>{{ item.value }}</span> {{ item.title }}
					</component>
				</li>
			</ul>
		</div>
	</div>

	<div v-if="!isLoading && (['no_access', 'disabled', 'promo', 'onboard', 'error'].includes(data.state))" class="product" :class="[title.replaceAll(' ', '-')]">
		<div class="product-top">
			<div class="product-icon">
				<span class="icon">
					<i />
				</span>
			</div>
			<div class="product-action">
				<span v-if="data?.env" class="badge badge-danger mr-5 env-badge" v-text="data?.env" />
				<button v-if="hasButtonLink" class="btn action disabled" :disabled="actionButtionDisabled" @click="runAction()">
					{{ buttonLabel }} <i v-if="buttonLabel === 'Learn'" class="fas fa-arrow-right" />
				</button>
			</div>
		</div>
		<div>
			<div class="product-details">
				<p class="product-info">
					{{ text }}
				</p>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { computed, ref } from 'vue';

	const props = defineProps<{
		isLoading: boolean,
		title: string,
		data: any,
	}>();

	const emit = defineEmits<{
		request: [link: string],
	}>();

	const actionButtionDisabled = ref(false);

	const text = computed(() => {
		return props.data[`${props.data.state}_text`] || '';
	});

	const hasButtonLink = computed(() => {
		return `${props.data.state}_button_link` in props.data;
	});

	const buttonLink = computed(() => {
		return props.data[`${props.data.state}_button_link`] || '';
	});

	const buttonLabel = computed(() => {
		return props.data[`${props.data.state}_button_text`] || '';
	});

	const getLinkType = (item: any) => {
		if (props.data.state === 'reporting') {
			return 'div';
		}

		if (item.link !== undefined && item.link.startsWith('http')) {
			return 'a';
		} else if (item.link !== undefined && !item.link.startsWith('http')) {
			return 'router-link';
		}

		if (props.data.open_link !== undefined && props.data.open_link.startsWith('http')) {
			return 'a';
		} else if (props.data.open_link !== undefined && !props.data.open_link.startsWith('http')) {
			return 'router-link';
		}

		return 'router-link';
	};

	const runAction = () => {
		actionButtionDisabled.value = true;

		if (buttonLink.value.startsWith('http')) {
			window.open(buttonLink.value, '_blank', 'noopener');
			actionButtionDisabled.value = false;
			return;
		}

		emit('request', buttonLink.value);

		setTimeout(() => {
			actionButtionDisabled.value = false;
		}, 5000);
	};
</script>

<style lang="scss" scoped>
	@use '../../assets/styles/base' as base;

	.loader * {
		filter: grayscale(1) blur(0.75px);
		animation: loader 4s infinite ease-in-out;

		div.loader {
			display: block;
			width: 30%;
			height: 24px;
			background: silver;
			margin: 20px 0 15px 70px;
			border-radius: 2px;
		}

		li.loader {
			width: 90%;
			height: 15px;
			display: block;
			background: silver;
			margin: 10px 0 10px 0;
			border-radius: 2px;
		}

		li.loader:last-of-type {
			width: 80%;
			margin-bottom: 30px;
		}
	}

	.product {
		display: flex;
		flex-direction: column;
		background-color: white;
		border-radius: 0.5rem;
		box-shadow: 0 3px 11px rgba(0, 0, 0, 0.05);
		transition: 0.35s all ease-in-out;

		.product-top {
			display: flex;

			@media (max-width: 1490px) and (min-width: 575px) {
				display: grid;
				grid-template-columns: 1fr;
			}

			.product-icon {
				flex: 1 0 auto;
			}

			.product-action {
				margin-left: 0;
				margin-right: 1rem;
				display: flex;
				align-items: center;

				@media (max-width: 1490px) and (min-width: 575px) {
					margin-top: 0;
					margin-left: 1.3rem;
					margin-bottom: 0;
				}

				.btn {
					border-radius: 1.125rem;
					background: transparent;
					border: 2px solid #bfbfbf;
					padding: 5px 20px;

					&:hover {
						background-color: #e3e3e3;
					}

					&.action {
						color: base.$primary-colour;
					}
				}

				.env-badge {
					text-transform: capitalize;
				}
			}

			+ .product-details {
				margin-bottom: 1rem;
				margin-left: calc(1rem - 5px);
				margin-right: calc(1rem - 5px);

				.product-list {
					display: grid;
					grid-template-columns: repeat(2, 1fr);
					grid-template-rows: repeat(2, 1fr);

					li {
						a, .product-info-row {
							display: flex;
							align-items: center;
							font-size: 0.825rem;
							line-height: 14px;
							font-weight: bold;
							padding: 15px 5px;
							border-radius: 5px;
							color: #000033;
							transition: 0.35s all ease-in-out;

							span {
								font-size: 18px;
								min-width: 30px;
								max-width: fit-content;
								height: 30px;
								display: flex;
								align-items: center;
								justify-content: center;
								border-radius: 5px;
								margin-right: 5px;
								padding: 0 3px;
							}

							&:hover {
								text-decoration: none;
							}
						}
					}
				}
			}
		}

		&.Create .product-top + .product-details .product-list li a,
		&.Create .product-top + .product-details .product-list li .product-info-row {
			&:hover {
				background: #f3f4fe;
			}

			span {
				background: #dde1fc;
			}
		}

		&.RPA .product-top + .product-details .product-list li a,
		&.RPA .product-top + .product-details .product-list li .product-info-row {
			&:hover {
				background: rgba(#efe9f1, 0.4);
			}

			span {
				background: #efe9f1;
				color: #000033;
			}
		}

		&.AI .product-top + .product-details .product-list li a,
		&.AI .product-top + .product-details .product-list li .product-info-row {
			&:hover {
				background: #faf2f4;
			}

			span {
				background: #f6e7eb;
				color: #000033;
			}
		}

		&.Converse-CX .product-top + .product-details .product-list li a,
		&.Converse-CX .product-top + .product-details .product-list li .product-info-row {
			&:hover {
				background: #f7f7f7;
			}

			span {
				background: #eae8eb;
			}
		}

		&.Spark .product-top + .product-details .product-list li a,
		&.Spark .product-top + .product-details .product-list li .product-info-row {
			&:hover {
				background: #eef6f8;
			}

			span {
				background: #e1f0f2;
				color: #000033;
			}
		}

		&.IDP .product-top + .product-details .product-list li a,
		&.IDP .product-top + .product-details .product-list li .product-info-row {
			&:hover {
				background: #faeeee;
			}

			span {
				background: #f8e7e7;
				color: #000033;
			}
		}

		.product-icon {
			.icon {
				display: flex;
				flex-direction: column;
				align-content: center;
				align-items: center;
				text-wrap: wrap;
				justify-content: center;
				padding: 1rem 1rem 0.5rem;
				border-radius: 10px;
				transition: all ease-in-out 0.5s;

				i {
					height: 34px;
					width: 100%;
					background-repeat: no-repeat;
					background-size: contain;
				}
			}
		}

		.product-details {
			margin-left: 20px;
			margin-right: 20px;

			.product-list {
				list-style: none;
				padding-inline-start: 0;

				a, .product-info-row {
					color: base.$secondary-text-colour;
					font-size: 14px;
					line-height: 20px;
				}

				a {
					&:hover {
						text-decoration: underline;
					}
				}
			}
		}

		.product-action {
			margin-left: 58px;
			display: inline-block;
			margin-bottom: 0.875rem;
			margin-top: 1.125rem;

			.btn {
				display: inline-block;
			}
		}

		&.Create .icon i {
			background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='40' viewBox='0 0 120 40'%3E%3Cg id='Rectangle_3552' data-name='Rectangle 3552' fill='%234863f0' stroke='%23707070' stroke-width='1' opacity='0.121'%3E%3Crect width='40' height='40' rx='8' stroke='none'/%3E%3Crect x='0.5' y='0.5' width='39' height='39' rx='7.5' fill='none'/%3E%3C/g%3E%3Cg id='Group_8114' data-name='Group 8114' transform='translate(8.199 9.532)'%3E%3Cpath id='Path_1' data-name='Path 1' d='M80.4,27.665c0-5.549,3.039-9.165,7.759-9.165a6.822,6.822,0,0,1,7.006,6.277l-3.641.6c-.376-2.234-1.581-3.489-3.365-3.489-2.536,0-4.042,2.159-4.042,5.774s1.505,5.775,4.042,5.775c1.858,0,3.063-1.306,3.415-3.767l3.641.629c-.451,3.967-3.214,6.527-7.056,6.527-4.72,0-7.759-3.616-7.759-9.165' transform='translate(-41.652 -17.147)' fill='%23003'/%3E%3Cpath id='Path_2' data-name='Path 2' d='M104.628,25.2h3.365v1.179h.05a3.272,3.272,0,0,1,2.864-1.432,6.164,6.164,0,0,1,1.732.226l-.6,3.415a5.5,5.5,0,0,0-1.482-.2c-1.532,0-2.335,1.03-2.335,2.964V38.5h-3.592Z' transform='translate(-49.361 -19.198)' fill='%23003'/%3E%3Cpath id='Path_3' data-name='Path 3' d='M116.557,31.791c0-4.143,2.585-7.031,6.277-7.031,3.717,0,6.253,2.838,6.253,7.031v1.055h-8.914c.1,1.884,1.105,2.962,2.737,2.962a3.751,3.751,0,0,0,3.214-1.983l2.536,1.983a6.414,6.414,0,0,1-5.75,3.013c-3.767,0-6.352-2.812-6.352-7.031m9.039-1.48a2.52,2.52,0,0,0-2.737-2.537,2.609,2.609,0,0,0-2.687,2.537Z' transform='translate(-53.157 -19.139)' fill='%23003'/%3E%3Cpath id='Path_4' data-name='Path 4' d='M136.848,34.829c0-2.235,1.331-3.44,4.745-4.268,2.11-.5,2.763-.879,2.763-1.583,0-.778-.678-1.229-1.833-1.229a2.406,2.406,0,0,0-2.536,2.134l-3.289-.728c.4-2.636,2.762-4.395,5.825-4.395,3.339,0,5.449,1.935,5.449,5.048v8.638h-3.214V37.391h-.051a4.069,4.069,0,0,1-3.489,1.43c-2.637,0-4.37-1.581-4.37-3.992m7.633-.879V32.017h-.05c-.226.4-.779.7-1.985,1.055-1.607.427-2.16.829-2.16,1.531,0,.8.553,1.205,1.633,1.205,1.507,0,2.561-.753,2.561-1.858' transform='translate(-59.566 -19.139)' fill='%23003'/%3E%3Cpath id='Path_5' data-name='Path 5' d='M160.539,26.92v5.625c0,.828.3,1.079,1.356,1.079h1.456v3.44h-1.809c-3.29,0-4.595-1.205-4.595-4.294V26.92h-2.285V23.755h2.285V20.416h3.591v3.339h3.289V26.92Z' transform='translate(-65.283 -17.757)' fill='%23003'/%3E%3Cpath id='Path_6' data-name='Path 6' d='M169.171,31.791c0-4.143,2.585-7.031,6.277-7.031,3.717,0,6.253,2.838,6.253,7.031v1.055h-8.914c.1,1.884,1.105,2.962,2.737,2.962a3.751,3.751,0,0,0,3.214-1.983l2.536,1.983a6.414,6.414,0,0,1-5.75,3.013c-3.767,0-6.352-2.812-6.352-7.031m9.039-1.48a2.52,2.52,0,0,0-2.737-2.537,2.609,2.609,0,0,0-2.687,2.537Z' transform='translate(-69.899 -19.139)' fill='%23003'/%3E%3Cpath id='Path_7' data-name='Path 7' d='M52.994,39.3H50.969V37.27a.863.863,0,1,0-1.726,0V39.3H47.218a.863.863,0,1,0,0,1.726h2.025v2.025a.863.863,0,1,0,1.726,0V41.021h2.025a.863.863,0,1,0,0-1.726' transform='translate(-30.818 -22.846)' fill='%234863f0'/%3E%3Cpath id='Path_8' data-name='Path 8' d='M43.545,16.516H26.9a3.338,3.338,0,0,0-3.335,3.335V34.512A3.338,3.338,0,0,0,26.9,37.847h9.185v-9.8H46.879V19.851a3.338,3.338,0,0,0-3.334-3.335M34.36,36.191H26.9a1.68,1.68,0,0,1-1.677-1.679V28.044H34.36Zm0-9.872H25.223V19.851A1.679,1.679,0,0,1,26.9,18.173H34.36Zm10.864,0H36.086V18.173h7.459a1.68,1.68,0,0,1,1.679,1.678Z' transform='translate(-23.566 -16.516)' fill='%234863f0'/%3E%3C/g%3E%3C/svg%3E%0A");
		}

		&.RPA .icon i {
			background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='91.555' height='40' viewBox='0 0 91.555 40'%3E%3Cg id='Group_8118' data-name='Group 8118' transform='translate(-143 -767)'%3E%3Cg id='Group_4' data-name='Group 4' transform='translate(148.739 769.896)'%3E%3Cpath id='Path_19' data-name='Path 19' d='M94.989,36.993H91.046l-3.272-6.1H85.543v6.1H81.874V19.64h7.064c3.67,0,5.926,2.131,5.926,5.6a5.275,5.275,0,0,1-3.4,5.231V30.5Zm-6.5-9.445c1.76,0,2.7-.819,2.7-2.33,0-1.487-.917-2.231-2.7-2.231h-2.95v4.561Z' transform='translate(-39.161 -11.919)' fill='%23003'/%3E%3Cpath id='Path_20' data-name='Path 20' d='M104.811,19.64h7.116c3.694,0,6.023,2.156,6.023,5.726,0,3.594-2.28,5.7-6.074,5.7h-3.4v5.924h-3.67Zm7.016,8.057a2.253,2.253,0,0,0,2.454-2.356,2.121,2.121,0,0,0-2.429-2.355h-3.372V27.7Z' transform='translate(-46.659 -11.919)' fill='%23003'/%3E%3Cpath id='Path_21' data-name='Path 21' d='M133.69,34.291h-6.643l-.917,2.7h-3.645l6.2-17.353h3.372l6.2,17.353h-3.644Zm-1.115-3.222-2.182-6.4h-.049l-2.182,6.4Z' transform='translate(-52.436 -11.919)' fill='%23003'/%3E%3Cpath id='Path_22' data-name='Path 22' d='M43.959,19.411V17.864a3.3,3.3,0,0,0-3.292-3.292H33.234V13.1a2.533,2.533,0,1,0-1.568,0v1.47H24.234a3.3,3.3,0,0,0-3.292,3.292v1.548A2.814,2.814,0,0,0,18.42,22.2v4.789a2.814,2.814,0,0,0,2.522,2.79v2.558a3.3,3.3,0,0,0,3.292,3.292H40.666a3.3,3.3,0,0,0,3.292-3.292V29.781a2.814,2.814,0,0,0,2.522-2.79V22.2a2.814,2.814,0,0,0-2.522-2.79M32.45,9.738a.966.966,0,1,1-.966.967.967.967,0,0,1,.966-.967M19.919,26.991V22.2a1.3,1.3,0,0,1,1.022-1.261v7.311a1.3,1.3,0,0,1-1.022-1.261m22.4,5.348A1.659,1.659,0,0,1,40.666,34H24.234a1.659,1.659,0,0,1-1.657-1.657V17.864a1.659,1.659,0,0,1,1.657-1.657H40.666a1.659,1.659,0,0,1,1.657,1.657Zm2.658-5.348a1.3,1.3,0,0,1-1.022,1.261V20.941A1.3,1.3,0,0,1,44.981,22.2Z' transform='translate(-18.42 -8.17)' fill='%23814a7b'/%3E%3Cpath id='Path_23' data-name='Path 23' d='M35.3,27.9a2.687,2.687,0,1,0-2.687,2.686A2.69,2.69,0,0,0,35.3,27.9m-3.73,0a1.043,1.043,0,1,1,1.043,1.042A1.045,1.045,0,0,1,31.572,27.9' transform='translate(-22.182 -13.741)' fill='%23814a7b'/%3E%3Cpath id='Path_24' data-name='Path 24' d='M43.646,25.213A2.687,2.687,0,1,0,46.331,27.9a2.691,2.691,0,0,0-2.685-2.688m0,3.73A1.043,1.043,0,1,1,44.688,27.9a1.044,1.044,0,0,1-1.043,1.042' transform='translate(-25.787 -13.741)' fill='%23814a7b'/%3E%3Cpath id='Path_25' data-name='Path 25' d='M41.306,37.149a.825.825,0,0,0-.582.249l-.012.013a5.164,5.164,0,0,1-3.752,1.594,5.107,5.107,0,0,1-3.7-1.6.81.81,0,0,0-.574-.254h-.008a.9.9,0,0,0-.58.226.823.823,0,0,0-.026,1.162,6.778,6.778,0,0,0,9.828,0,.823.823,0,0,0-.6-1.393' transform='translate(-22.809 -17.642)' fill='%23814a7b'/%3E%3C/g%3E%3Cg id='Rectangle_3485' data-name='Rectangle 3485' transform='translate(143 767)' fill='%23794d89' stroke='%23707070' stroke-width='1' opacity='0.121'%3E%3Crect width='40' height='40' rx='8' stroke='none'/%3E%3Crect x='0.5' y='0.5' width='39' height='39' rx='7.5' fill='none'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E%0A");
		}

		&.AI .icon i {
			background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='65.24' height='40' viewBox='0 0 65.24 40'%3E%3Cg id='Group_8115' data-name='Group 8115' transform='translate(6.536 7.023)'%3E%3Cpath id='Path_15' data-name='Path 15' d='M89.871,34.162H83.5l-.879,2.592h-3.5l5.945-16.646H88.3l5.945,16.646H90.751ZM88.8,31.071l-2.093-6.136h-.047l-2.093,6.136Z' transform='translate(-40.58 -15.552)' fill='%23003'/%3E%3Crect id='Rectangle_30' data-name='Rectangle 30' width='3.52' height='16.646' transform='translate(55.184 4.556)' fill='%23003'/%3E%3Cpath id='Path_16' data-name='Path 16' d='M45.688,24.785a.784.784,0,1,0,0-1.568h-1.7V20.926h1.7a.784.784,0,1,0,0-1.568h-1.7v-.37a3.161,3.161,0,0,0-3.158-3.157H39.522v-2a.784.784,0,0,0-1.569,0v2H35.665v-2a.784.784,0,0,0-1.569,0v2H31.805v-2a.784.784,0,0,0-1.569,0v2h-2.29v-2a.784.784,0,0,0-1.569,0v2H25.069a3.161,3.161,0,0,0-3.158,3.157v.37h-1.7a.784.784,0,1,0,0,1.568h1.7v2.291h-1.7a.784.784,0,1,0,0,1.568h1.7v2.291h-1.7a.784.784,0,1,0,0,1.568h1.7v2.291h-1.7a.784.784,0,1,0,0,1.568h1.7v.369a3.161,3.161,0,0,0,3.158,3.157h1.309v2a.784.784,0,0,0,1.569,0v-2h2.29v2a.784.784,0,0,0,1.569,0v-2H34.1v2a.784.784,0,0,0,1.569,0v-2h2.288v2a.784.784,0,0,0,1.569,0v-2h1.309a3.161,3.161,0,0,0,3.158-3.157V32.5h1.7a.784.784,0,1,0,0-1.568h-1.7V28.644h1.7a.784.784,0,1,0,0-1.568h-1.7V24.785Zm-3.268,8.087a1.591,1.591,0,0,1-1.59,1.59H25.069a1.591,1.591,0,0,1-1.588-1.59V18.988a1.591,1.591,0,0,1,1.588-1.59H40.831a1.591,1.591,0,0,1,1.59,1.59Z' transform='translate(-19.428 -13.051)' fill='%23ce2d4f'/%3E%3Cpath id='Path_17' data-name='Path 17' d='M36.493,26.913a.771.771,0,0,0-1.387,0l-2.8,7a.747.747,0,0,0,.416.969.749.749,0,0,0,.571-.008.739.739,0,0,0,.4-.407l.652-1.63h2.907l.652,1.629a.746.746,0,1,0,1.386-.555Zm-1.551,4.428L35.8,29.2l.857,2.14Z' transform='translate(-23.973 -17.809)' fill='%23ce2d4f'/%3E%3Cpath id='Path_18' data-name='Path 18' d='M46,26.424a.747.747,0,0,0-.747.747v7a.747.747,0,0,0,1.493,0v-7A.747.747,0,0,0,46,26.424' transform='translate(-28.58 -17.79)' fill='%23ce2d4f'/%3E%3C/g%3E%3Cg id='Rectangle_3554' data-name='Rectangle 3554' fill='%23b53858' stroke='%23707070' stroke-width='1' opacity='0.121'%3E%3Crect width='40' height='40' rx='8' stroke='none'/%3E%3Crect x='0.5' y='0.5' width='39' height='39' rx='7.5' fill='none'/%3E%3C/g%3E%3C/svg%3E%0A");
		}

		&.Converse-CX .icon i {
			background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' width='190' height='40' viewBox='0 0 190 40'%3E%3Cdefs%3E%3CclipPath id='clip-path'%3E%3Crect id='Rectangle_33' data-name='Rectangle 33' width='180.998' height='23.818' fill='none'/%3E%3C/clipPath%3E%3C/defs%3E%3Cg id='Group_8116' data-name='Group 8116' transform='translate(-867 -766)'%3E%3Cg id='Group_8' data-name='Group 8' transform='translate(876.002 775.545)'%3E%3Cg id='Group_7' data-name='Group 7' clip-path='url(%23clip-path)'%3E%3Cpath id='Path_39' data-name='Path 39' d='M57.1,10.86c0-5.45,2.984-9,7.62-9A6.7,6.7,0,0,1,71.6,8.023l-3.576.592c-.369-2.194-1.553-3.426-3.3-3.426-2.491,0-3.969,2.12-3.969,5.671s1.478,5.672,3.969,5.672c1.825,0,3.008-1.283,3.354-3.7l3.576.617c-.443,3.9-3.156,6.411-6.93,6.411-4.636,0-7.62-3.551-7.62-9' transform='translate(-18.867 -0.614)' fill='%23003'/%3E%3Cpath id='Path_40' data-name='Path 40' d='M227.182,10.86c0-5.45,2.984-9,7.62-9a6.7,6.7,0,0,1,6.881,6.164l-3.576.592c-.369-2.194-1.553-3.426-3.3-3.426-2.491,0-3.969,2.12-3.969,5.671s1.478,5.672,3.969,5.672c1.825,0,3.008-1.283,3.353-3.7l3.576.617c-.443,3.9-3.156,6.411-6.93,6.411-4.636,0-7.62-3.551-7.62-9' transform='translate(-75.063 -0.614)' fill='%23003'/%3E%3Cpath id='Path_41' data-name='Path 41' d='M80.555,15.024c0-4.118,2.639-6.9,6.462-6.9,3.846,0,6.485,2.787,6.485,6.9s-2.639,6.9-6.485,6.9c-3.823,0-6.462-2.785-6.462-6.9m9.42,0c0-2.342-1.085-3.7-2.959-3.7-1.85,0-2.934,1.356-2.934,3.7s1.084,3.7,2.934,3.7c1.874,0,2.959-1.356,2.959-3.7' transform='translate(-26.616 -2.683)' fill='%23003'/%3E%3Cpath id='Path_42' data-name='Path 42' d='M102.792,8.49h3.354V9.648h.049a4.707,4.707,0,0,1,3.6-1.529c2.86,0,4.784,1.973,4.784,5.081v8.36h-3.526V13.817a2.178,2.178,0,0,0-2.368-2.441,2.223,2.223,0,0,0-2.367,2.441V21.56h-3.527Z' transform='translate(-33.964 -2.683)' fill='%23003'/%3E%3Cpath id='Path_43' data-name='Path 43' d='M120.577,8.673h3.748l3.3,8.458h.05l3.3-8.458h3.748l-5.45,13.07h-3.256Z' transform='translate(-39.84 -2.866)' fill='%23003'/%3E%3Cpath id='Path_44' data-name='Path 44' d='M141.711,15.024c0-4.068,2.539-6.9,6.164-6.9,3.651,0,6.141,2.787,6.141,6.9V16.06h-8.754c.1,1.85,1.085,2.909,2.688,2.909a3.684,3.684,0,0,0,3.156-1.948L153.6,18.97a6.3,6.3,0,0,1-5.647,2.959c-3.7,0-6.239-2.762-6.239-6.9m8.877-1.454a2.475,2.475,0,0,0-2.688-2.492,2.562,2.562,0,0,0-2.639,2.492Z' transform='translate(-46.823 -2.683)' fill='%23003'/%3E%3Cpath id='Path_45' data-name='Path 45' d='M162.992,8.551h3.3V9.709h.049A3.213,3.213,0,0,1,169.156,8.3a6.069,6.069,0,0,1,1.7.222l-.593,3.354a5.4,5.4,0,0,0-1.455-.2c-1.5,0-2.293,1.012-2.293,2.911V21.62h-3.527Z' transform='translate(-53.854 -2.743)' fill='%23003'/%3E%3Cpath id='Path_46' data-name='Path 46' d='M174.553,17.342l3.131-.838c.42,1.75,1.307,2.54,2.984,2.54,1.233,0,1.973-.394,1.973-1.06,0-1.036-1.283-1.405-2.836-1.9-2.219-.615-4.563-1.381-4.563-4.02,0-2.244,2.1-3.945,4.932-3.945a5.507,5.507,0,0,1,5.524,3.847l-3.106.913a2.478,2.478,0,0,0-2.418-1.9c-1.035,0-1.651.444-1.651,1.035,0,.667.985.938,2.342,1.332,2.268.641,5.055,1.479,5.055,4.513,0,2.441-2.072,4.068-5.253,4.068-3.329,0-5.549-1.627-6.115-4.587' transform='translate(-57.674 -2.683)' fill='%23003'/%3E%3Cpath id='Path_47' data-name='Path 47' d='M193.293,15.024c0-4.068,2.539-6.9,6.164-6.9,3.651,0,6.141,2.787,6.141,6.9V16.06h-8.754c.1,1.85,1.085,2.909,2.688,2.909a3.684,3.684,0,0,0,3.156-1.948l2.491,1.948a6.3,6.3,0,0,1-5.647,2.959c-3.7,0-6.239-2.762-6.239-6.9m8.877-1.454a2.475,2.475,0,0,0-2.688-2.492,2.562,2.562,0,0,0-2.639,2.492Z' transform='translate(-63.866 -2.683)' fill='%23003'/%3E%3Cpath id='Path_48' data-name='Path 48' d='M253.168,10.914,247.86,2.4h4.1l3.284,5.825h.049L258.6,2.4h4.1l-5.307,8.517,5.455,8.763h-4.122l-3.432-6.1h-.049l-3.431,6.1h-4.1Z' transform='translate(-81.847 -0.792)' fill='%23003'/%3E%3Cpath id='Path_49' data-name='Path 49' d='M14.392,11.252a1.589,1.589,0,0,1-.082-.2,1.708,1.708,0,0,0-.1-.251l-.031-.042a.562.562,0,0,1-.053-.074,1.771,1.771,0,0,0-.617-.589l-.009-.037L13.426,10a1.944,1.944,0,0,0-1-.3,2.171,2.171,0,1,0,0,4.342.726.726,0,0,0,.219-.034,3.957,3.957,0,0,1-2.028,2.354l-.04.029a.952.952,0,0,0-.322.666.788.788,0,0,0,.806.806.506.506,0,0,0,.372-.121,5.64,5.64,0,0,0,3.253-5.172c-.025-.144-.042-.275-.058-.4a2.778,2.778,0,0,0-.23-.924' transform='translate(-3.387 -3.206)' fill='%23141930'/%3E%3Cpath id='Path_50' data-name='Path 50' d='M21.783,11.049a1.79,1.79,0,0,0-.1-.252l-.031-.042a.654.654,0,0,1-.053-.074,1.779,1.779,0,0,0-.617-.589l-.009-.037L20.9,10a1.94,1.94,0,0,0-1-.3,2.171,2.171,0,1,0,0,4.342.726.726,0,0,0,.22-.035,3.951,3.951,0,0,1-2.023,2.352.705.705,0,0,0-.368.7.79.79,0,0,0,.807.807.5.5,0,0,0,.371-.121A5.634,5.634,0,0,0,22.155,12.6a2.8,2.8,0,0,0-.291-1.349,1.5,1.5,0,0,1-.082-.2' transform='translate(-5.856 -3.206)' fill='%23141930'/%3E%3Cpath id='Path_51' data-name='Path 51' d='M4.35,23.818a.81.81,0,0,1-.814-.813V20.947H3.275A3.278,3.278,0,0,1,0,17.673V3.274A3.278,3.278,0,0,1,3.275,0H19.622A3.278,3.278,0,0,1,22.9,3.274v14.4a3.278,3.278,0,0,1-3.275,3.274H8.027l-3.15,2.677a.817.817,0,0,1-.527.194M3.275,1.626A1.65,1.65,0,0,0,1.627,3.274v14.4a1.651,1.651,0,0,0,1.648,1.649H4.35a.813.813,0,0,1,.814.813v1.112L7.2,19.515a.809.809,0,0,1,.527-.193H19.622a1.651,1.651,0,0,0,1.648-1.649V3.274a1.65,1.65,0,0,0-1.648-1.649Z' fill='%23141930'/%3E%3C/g%3E%3C/g%3E%3Cg id='Rectangle_3488' data-name='Rectangle 3488' transform='translate(867 766)' fill='%23291c31' stroke='%23707070' stroke-width='1' opacity='0.121'%3E%3Crect width='40' height='40' rx='8' stroke='none'/%3E%3Crect x='0.5' y='0.5' width='39' height='39' rx='7.5' fill='none'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E%0A");
		}

		&.Spark .icon i {
			background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='110.337' height='40' viewBox='0 0 110.337 40'%3E%3Cg id='Rectangle_3553' data-name='Rectangle 3553' fill='%23098697' stroke='%23707070' stroke-width='1' opacity='0.121'%3E%3Crect width='40' height='40' rx='8' stroke='none'/%3E%3Crect x='0.5' y='0.5' width='39' height='39' rx='7.5' fill='none'/%3E%3C/g%3E%3Cg id='Group_2' data-name='Group 2' transform='translate(8.675 7.606)'%3E%3Cpath id='Path_9' data-name='Path 9' d='M80.035,30.765l3.528-.755c.439,2.531,1.461,3.5,3.723,3.5,1.679,0,2.629-.607,2.629-1.679,0-1.168-.829-1.8-3.651-2.749-3.966-1.339-5.45-2.775-5.45-5.281,0-2.847,2.359-4.819,5.767-4.819,3.358,0,5.937,1.995,6.473,5.013l-3.5.754c-.462-1.727-1.411-2.531-2.968-2.531-1.338,0-2.215.609-2.215,1.557,0,.877.73,1.362,3.261,2.215,4.234,1.46,5.888,3.091,5.888,5.84,0,2.945-2.531,4.915-6.278,4.915-4.064,0-6.594-2.091-7.2-5.985' transform='translate(-41.797 -15.052)' fill='%23003'/%3E%3Cpath id='Path_10' data-name='Path 10' d='M102.973,25.614h3.334v.9h.048a4.83,4.83,0,0,1,3.431-1.266c3.358,0,5.767,2.872,5.767,6.813,0,3.966-2.408,6.813-5.742,6.813a4.472,4.472,0,0,1-3.31-1.265h-.048v6.376h-3.48Zm9.1,6.447c0-2.311-1.047-3.625-2.945-3.625a2.587,2.587,0,0,0-2.676,2.7v1.849a2.621,2.621,0,0,0,2.676,2.726c1.874,0,2.945-1.363,2.945-3.651' transform='translate(-49.579 -17.176)' fill='%23003'/%3E%3Cpath id='Path_11' data-name='Path 11' d='M123.921,35.005c0-2.166,1.29-3.334,4.6-4.136,2.044-.488,2.677-.852,2.677-1.534,0-.754-.657-1.191-1.777-1.191a2.332,2.332,0,0,0-2.458,2.068l-3.187-.706c.389-2.555,2.677-4.259,5.645-4.259,3.236,0,5.281,1.874,5.281,4.892V38.51h-3.115V37.488h-.048a3.948,3.948,0,0,1-3.382,1.386c-2.556,0-4.235-1.532-4.235-3.869m7.4-.852V32.28h-.048c-.219.39-.755.681-1.923,1.022-1.557.414-2.092.8-2.092,1.484,0,.779.535,1.168,1.581,1.168,1.461,0,2.482-.729,2.482-1.8' transform='translate(-56.637 -17.175)' fill='%23003'/%3E%3Cpath id='Path_12' data-name='Path 12' d='M143.768,25.676h3.261v1.143h.048a3.17,3.17,0,0,1,2.775-1.386,5.976,5.976,0,0,1,1.679.218l-.584,3.31a5.329,5.329,0,0,0-1.436-.195c-1.485,0-2.263,1-2.263,2.872v6.935h-3.481Z' transform='translate(-63.42 -17.238)' fill='%23003'/%3E%3Cpath id='Path_13' data-name='Path 13' d='M157.538,18.436h3.481v9.733h.048L165.2,23.3h4.43l-4.649,5.158,4.77,7.738h-3.918l-3.065-5.426h-.05l-1.7,2V36.2h-3.481Z' transform='translate(-68.092 -14.864)' fill='%23003'/%3E%3Cpath id='Path_14' data-name='Path 14' d='M43.95,23.379a.809.809,0,0,0,.81-.806V18.2a3.106,3.106,0,0,0-3.111-3.094H34.954l-1.839-1.839A.8.8,0,1,0,31.979,14.4l.705.706H25.274A3.106,3.106,0,0,0,22.163,18.2v5.173a3.106,3.106,0,0,0,3.111,3.094h4.282v.941a1.833,1.833,0,0,0,1.831,1.83h4.39a1.832,1.832,0,0,0,1.831-1.83v-.941h4.042a1.487,1.487,0,0,1,1.491,1.48v4.705a1.487,1.487,0,0,1-1.491,1.48H34.944L33.115,32.3a.8.8,0,1,0-1.135,1.134l.694.694h-7.4a1.486,1.486,0,0,1-1.49-1.48V28.5a.81.81,0,0,0-1.621,0v4.147a3.105,3.105,0,0,0,3.111,3.092h7.4l-.7.7a.8.8,0,0,0,1.135,1.134l1.83-1.83h6.7a3.105,3.105,0,0,0,3.111-3.092V27.946a3.105,3.105,0,0,0-3.111-3.092H37.607V23.838a1.832,1.832,0,0,0-1.831-1.83h-4.39a1.833,1.833,0,0,0-1.831,1.83v1.016H25.274a1.488,1.488,0,0,1-1.49-1.481V18.2a1.487,1.487,0,0,1,1.49-1.483h7.392l-.686.686a.8.8,0,0,0,1.135,1.134l1.82-1.82h6.714A1.488,1.488,0,0,1,43.139,18.2v4.374a.809.809,0,0,0,.811.806M31.161,26.466V23.838a.227.227,0,0,1,.225-.226h4.39a.227.227,0,0,1,.225.226v3.569a.226.226,0,0,1-.225.226h-4.39a.226.226,0,0,1-.225-.226Z' transform='translate(-22.163 -13.031)' fill='%23008b8b'/%3E%3C/g%3E%3C/svg%3E%0A");
		}

		&.IDP .icon i {
			background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' width='86.206' height='40' viewBox='0 0 86.206 40'%3E%3Cdefs%3E%3CclipPath id='clip-path'%3E%3Crect id='Rectangle_32' data-name='Rectangle 32' width='77.138' height='20.937' fill='none'/%3E%3C/clipPath%3E%3C/defs%3E%3Cg id='Group_8117' data-name='Group 8117' transform='translate(-502 -767)'%3E%3Cg id='Group_6' data-name='Group 6' transform='translate(511.068 776.294)'%3E%3Cg id='Group_5' data-name='Group 5' transform='translate(0 0)' clip-path='url(%23clip-path)'%3E%3Cpath id='Path_26' data-name='Path 26' d='M0,12.189l0,0,.456-.346Z' transform='translate(0 -3.876)'/%3E%3Cpath id='Path_27' data-name='Path 27' d='M0,12.362l0,0Z' transform='translate(0 -4.044)'/%3E%3Cpath id='Path_28' data-name='Path 28' d='M40.295,18.156l0,0v-.005Z' transform='translate(-13.186 -5.941)'/%3E%3Cpath id='Path_29' data-name='Path 29' d='M.682,11.757l.244-.182v0L.9,11.589Z' transform='translate(-0.223 -3.787)'/%3E%3Crect id='Rectangle_31' data-name='Rectangle 31' width='3.685' height='17.425' transform='translate(41.691 1.341)'/%3E%3Cpath id='Path_30' data-name='Path 30' d='M71.849,1.994H77.9c4.631,0,7.642,3.286,7.642,8.712,0,5.477-3.011,8.713-7.667,8.713H71.849Zm6.025,14.04c2.439,0,3.983-1.767,3.983-5.328,0-3.535-1.543-5.327-3.983-5.327H75.533V16.034Z' transform='translate(-23.512 -0.652)'/%3E%3Cpath id='Path_31' data-name='Path 31' d='M95.047,1.994h7.145c3.71,0,6.049,2.165,6.049,5.749,0,3.61-2.29,5.726-6.1,5.726h-3.41v5.95H95.047Zm7.046,8.09a2.262,2.262,0,0,0,2.464-2.365,2.13,2.13,0,0,0-2.44-2.364H98.732v4.729Z' transform='translate(-31.103 -0.652)'/%3E%3Cpath id='Path_32' data-name='Path 32' d='M16.321,20.937H6.554A3.345,3.345,0,0,1,3.213,17.6V3.341A3.345,3.345,0,0,1,6.554,0h9.767a3.344,3.344,0,0,1,3.34,3.341V17.6a3.345,3.345,0,0,1-3.34,3.342M6.554,1.682A1.661,1.661,0,0,0,4.895,3.341V17.6a1.662,1.662,0,0,0,1.659,1.66h9.767a1.661,1.661,0,0,0,1.658-1.66V3.341a1.661,1.661,0,0,0-1.658-1.659Z' transform='translate(-1.051 0)' fill='%23cd3333'/%3E%3Cpath id='Path_33' data-name='Path 33' d='M21.981,3.19l.4-.229s.3-1.532.7-1.788A3.293,3.293,0,0,1,25.238.538V15.932a4.529,4.529,0,0,1-1.629-.3,2.409,2.409,0,0,1-1.272-1.559H18.372V3.114Z' transform='translate(-6.012 -0.171)' fill='%23fff'/%3E%3Cpath id='Path_34' data-name='Path 34' d='M25.605,15.588a7.076,7.076,0,0,1-1.156-.1,1.377,1.377,0,0,1-1.119-1.075l-.211-.961c-.158-.082-.313-.171-.463-.268l-.934.3a1.379,1.379,0,0,1-1.49-.427,6.9,6.9,0,0,1-.6-.852l-.118-.2a7.019,7.019,0,0,1-.44-.953,1.377,1.377,0,0,1,.381-1.507l.724-.659c0-.091-.007-.181-.007-.27s0-.178.007-.268l-.727-.663a1.374,1.374,0,0,1-.378-1.5,7.008,7.008,0,0,1,.433-.94l.122-.209a6.97,6.97,0,0,1,.608-.859,1.376,1.376,0,0,1,1.491-.429l.936.3c.151-.1.3-.186.464-.268l.21-.96a1.376,1.376,0,0,1,1.121-1.076,7.038,7.038,0,0,1,2.31,0,1.377,1.377,0,0,1,1.119,1.075l.21.961c.159.081.314.172.464.268l.936-.3a1.379,1.379,0,0,1,1.489.427,6.953,6.953,0,0,1,.6.852l.118.2a7.057,7.057,0,0,1,.44.953l.2.544-.131.114a1.389,1.389,0,0,1-.453.851l-.725.659c0,.089.007.179.007.268s0,.178-.007.267l.728.662a1.376,1.376,0,0,1,.377,1.5,6.785,6.785,0,0,1-.433.939l-.118.207a7.136,7.136,0,0,1-.61.861,1.373,1.373,0,0,1-1.491.429l-.935-.3q-.226.145-.464.268l-.211.96a1.377,1.377,0,0,1-1.121,1.076,7.035,7.035,0,0,1-1.154.1m-.674-1.724a5.387,5.387,0,0,0,1.349,0l.351-1.6.408-.168a3.764,3.764,0,0,0,.861-.5l.349-.268,1.565.5a5.155,5.155,0,0,0,.328-.482l.106-.182a5.045,5.045,0,0,0,.242-.5L29.271,9.552l.058-.437a3.706,3.706,0,0,0,0-1l-.058-.437,1.22-1.109a5.472,5.472,0,0,0-.25-.514l-.1-.178a5.055,5.055,0,0,0-.321-.473l-1.566.5L27.9,5.639a3.763,3.763,0,0,0-.86-.5l-.408-.168-.351-1.6a5.329,5.329,0,0,0-1.35,0l-.351,1.606-.408.168a3.739,3.739,0,0,0-.86.5l-.349.268-1.566-.5a5.088,5.088,0,0,0-.326.481l-.108.185a5.248,5.248,0,0,0-.241.5l1.218,1.107-.058.436a3.806,3.806,0,0,0-.034.5,3.871,3.871,0,0,0,.034.5l.057.435-1.217,1.107a5.376,5.376,0,0,0,.25.513l.1.178a5.137,5.137,0,0,0,.321.472l1.562-.5.35.268a3.764,3.764,0,0,0,.861.5l.408.168Zm1.555-.03h0Zm-1.762,0h0Zm4.96-1.846h0m.952-1.194h0Zm-10.054,0,0,0,0,0M20.576,6.44l0,0,0,0m.955-1.193h0M24.739,3.4h0m1.751,0h0Zm-.881,4.143a1.074,1.074,0,1,0,.931.537,1.078,1.078,0,0,0-.931-.537' transform='translate(-6.213 -0.539)' fill='%23cd3333'/%3E%3Cpath id='Path_35' data-name='Path 35' d='M13.38,7.71H8.908a.841.841,0,0,1,0-1.681H13.38a.841.841,0,0,1,0,1.681' transform='translate(-2.64 -1.973)' fill='%23cd3333'/%3E%3Cpath id='Path_36' data-name='Path 36' d='M13.38,12.5H8.908a.841.841,0,0,1,0-1.681H13.38a.841.841,0,0,1,0,1.681' transform='translate(-2.64 -3.54)' fill='%23cd3333'/%3E%3Cpath id='Path_37' data-name='Path 37' d='M13.38,17.288H8.908a.841.841,0,0,1,0-1.681H13.38a.841.841,0,0,1,0,1.681' transform='translate(-2.64 -5.107)' fill='%23cd3333'/%3E%3Cpath id='Path_38' data-name='Path 38' d='M16.826,22.076H8.908a.841.841,0,1,1,0-1.681h7.918a.841.841,0,0,1,0,1.681' transform='translate(-2.64 -6.674)' fill='%23cd3333'/%3E%3C/g%3E%3C/g%3E%3Cg id='Rectangle_3486' data-name='Rectangle 3486' transform='translate(502 767)' fill='%23c2363d' stroke='%23707070' stroke-width='1' opacity='0.121'%3E%3Crect width='40' height='40' rx='8' stroke='none'/%3E%3Crect x='0.5' y='0.5' width='39' height='39' rx='7.5' fill='none'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E%0A");
		}

		.product-info {
			padding-top: 10px;
			padding-bottom: 20px;
			color: #66615B;
			font-weight: 600;
			letter-spacing: -0.4px;
			line-height: 1.5;
			font-size: 0.9rem;
			margin: 0.5rem 0;
		}

		&:hover {
			box-shadow: rgba(0, 0, 0, 0.15) 0px 3px 8px;
			transform: translate(0px, -1px);
		}
	}
</style>
